/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Skeleton, Text } from '@/shared/ui';
import { documentMask } from '@/shared/utils/input-masks';
import { formatPhoneDisplay } from '@/shared/utils/format-phone';
import { useLeaseClientLabels, type LeaseClientLabel } from '../hooks/useLeaseClientLabels';
import type { Lease } from '../types/lease.types';

export interface LeasePartiesPanelProps {
  lease: Lease;
  agencyId: string;
}

/**
 * Initials for the avatar chip, from at most the first two words.
 *
 * @param name - The party's name.
 * @returns Up to two uppercase initials.
 */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * The Partes tab: the tenant and the landlords, side by side.
 *
 * It used to be one thin list inside a tall card, mostly empty space, with
 * the tenant reading "Documento não informado" until the name arrived. Each
 * person is now a card with their document, how to reach them, their share
 * and a way into their record, and stays a placeholder while loading.
 *
 * Read-only by design -- parties are set when the lease is opened, and the
 * lease PUT route drops `landlords` from its writable allowlist.
 *
 * @param props.lease - The lease whose parties to list.
 * @param props.agencyId - Agency owning the lease and its clients.
 * @returns The parties tab body.
 */
export function LeasePartiesPanel({ lease, agencyId }: LeasePartiesPanelProps) {
  const { t } = useTranslation('features/leases');
  const clientIds = useMemo(
    () => [lease.tenant_client_id, ...lease.landlords.map((l) => l.client_id)],
    [lease.tenant_client_id, lease.landlords],
  );
  const { labels } = useLeaseClientLabels(agencyId, clientIds);
  const profileOf = (clientId: string) => `/app/agencies/${agencyId}/clients/${clientId}`;

  return (
    <div data-testid="lease-parties-panel" className="grid grid-cols-1 gap-(--space-4) md:grid-cols-2">
      <PartySection title={t('detail.tenant')} empty={t('detail.parties.noTenant')}>
        {lease.tenant_client_id && (
          <PartyCard
            identity={labels[lease.tenant_client_id]}
            profileHref={profileOf(lease.tenant_client_id)}
          />
        )}
      </PartySection>

      <PartySection title={t('detail.parties.landlords')} empty={t('detail.parties.noLandlords')}>
        {lease.landlords.length > 0 && lease.landlords.map((landlord) => (
          <PartyCard
            key={landlord.client_id}
            identity={labels[landlord.client_id]}
            profileHref={profileOf(landlord.client_id)}
            shareBps={landlord.share_bps}
          />
        ))}
      </PartySection>
    </div>
  );
}

/**
 * One side of the tab: its title and its people, or a line saying none.
 *
 * @param props.title - "Inquilino" or "Proprietários".
 * @param props.empty - What to say when there is nobody.
 * @param props.children - The party cards.
 * @returns The section.
 */
function PartySection({ title, empty, children }: { title: string; empty: string; children: ReactNode }) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) : Boolean(children);
  return (
    <section className="flex flex-col gap-(--space-2)">
      <Text component="h3" className="m-0 text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-secondary)">
        {title}
      </Text>
      {hasChildren ? children : (
        <Text component="p" className="m-0 rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-(--space-4) py-(--space-4) text-(length:--text-meta) text-(--text-tertiary)">
          {empty}
        </Text>
      )}
    </section>
  );
}

/**
 * One person: avatar, name, document, contact, share and a link to the record.
 *
 * `identity` resolves live from the client registry, so it is `undefined`
 * while the lookup is in flight; the card shows placeholders meanwhile.
 *
 * @param props.identity - The resolved client, once loaded.
 * @param props.profileHref - Where "Abrir ficha" goes.
 * @param props.shareBps - The landlord's share in basis points; absent for the tenant.
 * @returns The card.
 */
function PartyCard({
  identity,
  profileHref,
  shareBps,
}: {
  identity?: LeaseClientLabel;
  profileHref: string;
  shareBps?: number;
}) {
  const { t } = useTranslation('features/leases');

  return (
    <article
      data-testid="lease-party-card"
      className="flex flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) px-(--space-4) py-(--space-4) shadow-[var(--shadow-sm)]"
    >
      <div className="flex items-start gap-(--space-3)">
        <Text
          component="span"
          aria-hidden="true"
          className="flex items-center justify-center shrink-0 w-10 h-10 rounded-full border border-(--border-subtle) bg-(--surface-tertiary) text-(length:--text-meta) [font-weight:var(--font-semibold)] text-(--text-secondary)"
        >
          {initialsOf(identity?.name ?? '')}
        </Text>
        <div className="flex flex-col gap-(--space-0-5) min-w-0 flex-1">
          {identity ? (
            <Text component="span" className="text-(length:--text-body) [font-weight:var(--font-semibold)] text-(--text-primary) break-words">
              {identity.name}
            </Text>
          ) : (
            <Skeleton height={16} width="60%" radius="var(--radius-sm)" />
          )}
          <PartyFacts identity={identity} />
        </div>
      </div>
      {shareBps !== undefined && <ShareBar shareBps={shareBps} />}
      <Link
        to={profileHref}
        className="self-start text-(length:--text-meta) [font-weight:var(--font-medium)] text-(--seed-base) hover:underline"
      >
        {t('detail.parties.openProfile')}
      </Link>
    </article>
  );
}

/**
 * The document and contact lines under a party's name.
 *
 * @param props.identity - The resolved client, once loaded.
 * @returns The lines, or placeholders while loading.
 */
function PartyFacts({ identity }: { identity?: LeaseClientLabel }) {
  const { t } = useTranslation('features/leases');
  if (!identity) return <Skeleton height={12} width="40%" radius="var(--radius-sm)" />;

  const facts = [
    identity.document ? documentMask(identity.document).masked : t('detail.parties.noDocument'),
    identity.phone ? formatPhoneDisplay(identity.phone) : '',
    identity.email,
  ].filter(Boolean);

  return (
    <>
      {facts.map((fact) => (
        <Text key={fact} component="span" className="text-(length:--text-meta) text-(--text-secondary) [overflow-wrap:anywhere]">
          {fact}
        </Text>
      ))}
    </>
  );
}

/**
 * The landlord's share, written out over a filled bar so a split reads at
 * a glance. It sits under the contact lines rather than beside the name,
 * where it squeezed an e-mail into breaking mid-word.
 *
 * @param props.shareBps - The share in basis points.
 * @returns The share line and its bar.
 */
function ShareBar({ shareBps }: { shareBps: number }) {
  const { t } = useTranslation('features/leases');
  const percent = Math.max(0, Math.min(100, shareBps / 100));
  return (
    <div className="flex flex-col gap-(--space-1-5)">
      <Text component="span" className="text-(length:--text-meta) [font-weight:var(--font-medium)] text-(--text-secondary) [font-variant-numeric:tabular-nums]">
        {t('detail.parties.share', { share: shareBps / 100 })}
      </Text>
      <div aria-hidden="true" className="h-1.5 w-full overflow-hidden rounded-full bg-(--surface-tertiary)">
        <div className="h-full rounded-full bg-(--seed-base)" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

export default LeasePartiesPanel;
